import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import {
  ArrowRight,
  LockKey,
  ArrowsClockwise,
  IdentificationCard,
  FingerprintSimple,
  Prohibit,
} from "@phosphor-icons/react";

const steps = [
  "Select AI model & role",
  "Fill the ID Card (or let the AI fill it)",
  "AI updates the card after each session",
  "Upload the card to a new AI iteration",
  "New AI resumes exactly where you left off",
];

const features = [
  {
    icon: IdentificationCard,
    title: "The AI ID Card",
    body: "A structured continuity file capturing project context, current state, next steps, constraints and decisions — everything a fresh AI needs.",
  },
  {
    icon: LockKey,
    title: "Single-Use Key",
    body: "Every license key (PRID) locks permanently to one email on activation. Once used, it can never unlock another account.",
  },
  {
    icon: FingerprintSimple,
    title: "Encrypted At Rest",
    body: "Card contents are AES-encrypted in storage. Unreadable without the protocol key — your handoff data stays protected.",
  },
  {
    icon: Prohibit,
    title: "Copy Protected",
    body: "The read-only handoff view is watermarked and blocks copy, paste and download — share it with an AI, not with thieves.",
  },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#050505] text-zinc-100">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-40 pb-24 overflow-hidden">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1705077111154-94a4a210461e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600)",
            backgroundSize: "cover",
            backgroundPosition: "center",
            maskImage: "linear-gradient(to bottom, black 0%, transparent 90%)",
            WebkitMaskImage: "linear-gradient(to bottom, black 0%, transparent 90%)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/70 to-transparent" />

        <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8 fadeup">
            <img
              src="/ai-logo.jpg"
              alt="AI ID Card"
              className="h-44 sm:h-56 w-auto object-contain logo-neon -ml-4 mb-2"
            />
            <p className="font-mono-p text-xs tracking-[0.3em] uppercase text-cyan-400 mb-6">
              // AI Continuity Protocol — Single or Multi AI
            </p>
            <h1 className="font-mono-p font-extrabold tracking-tighter uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
              Never lose your
              <br />
              <span className="text-cyan-400 drop-shadow-[0_0_18px_rgba(0,240,255,0.35)]">
                project
              </span>{" "}
              again.
            </h1>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-zinc-400">
              When your AI loops, resets or hits a session limit — your AI ID Card
              carries the memory forward. Update it after every session, hand it to
              the next iteration, and pick up with{" "}
              <span className="text-zinc-200">no loss of time, memory or production.</span>
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/register"
                data-testid="hero-activate-btn"
                className="group flex items-center gap-2 font-mono-p text-sm uppercase tracking-widest bg-cyan-400 text-black px-6 py-3.5 font-bold hover:bg-cyan-300 transition-colors"
              >
                Get Started — Free
                <ArrowRight size={16} weight="bold" className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/store"
                data-testid="hero-pricing-btn"
                className="font-mono-p text-sm uppercase tracking-widest border border-zinc-700 px-6 py-3.5 text-zinc-300 hover:border-cyan-400/50 hover:text-cyan-400 transition-colors"
              >
                View Pricing
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="relative max-w-7xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-800 border border-zinc-800">
          {features.map((f, i) => (
            <div
              key={f.title}
              data-testid={`feature-${i}`}
              className="bg-[#090A0C] p-8 hover:bg-[#0c0e12] transition-colors group"
            >
              <f.icon size={30} weight="duotone" className="text-cyan-400 mb-6" />
              <h3 className="font-mono-p font-bold uppercase tracking-tight text-lg mb-3">
                {f.title}
              </h3>
              <p className="text-sm text-zinc-500 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="relative max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-4">
          <p className="font-mono-p text-xs tracking-[0.3em] uppercase text-cyan-400 mb-4">
            // Protocol Flow
          </p>
          <h2 className="font-mono-p font-bold uppercase tracking-tight text-2xl sm:text-3xl lg:text-4xl leading-tight">
            How continuity
            <br />
            stays alive
          </h2>
          <div className="mt-6 flex items-center gap-3 text-zinc-500 text-sm">
            <ArrowsClockwise size={18} className="text-cyan-400" />
            Weekly rotation keeps the card fresh.
          </div>
        </div>
        <div className="lg:col-span-8">
          <ol className="space-y-px bg-zinc-800 border border-zinc-800">
            {steps.map((s, i) => (
              <li
                key={i}
                data-testid={`step-${i}`}
                className="bg-[#090A0C] flex items-center gap-6 p-6 hover:bg-[#0c0e12] transition-colors"
              >
                <span className="font-mono-p text-cyan-400 text-lg font-bold w-10 shrink-0">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-zinc-300">{s}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <footer className="border-t border-zinc-800 mt-10">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col sm:flex-row justify-between gap-4 items-center">
          <span className="font-mono-p text-xs uppercase tracking-widest text-zinc-600">
            AI Continuity Protocol
          </span>
          <span className="font-mono-p text-xs text-zinc-700">
            PRID-XXXX-XXXX-XXXX · Encrypted · Single-Use
          </span>
        </div>
      </footer>
    </div>
  );
}
